import type { Logger } from "@pepperstone/mfe-dev-kit/types";
import { DropdownList } from "@pepperstone/pepper-ui-kit";
import { InputField } from "@pepperstone/pepper-ui-kit/atoms/input-field/InputField";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnumFormFieldType, type FormField } from "@/api/cashier";
import {
  BankStatementUpload,
  type UploadedFile,
} from "@/components/bank-statement-upload/BankStatementUpload";
import {
  type NuveiCardFieldsChange,
  NuveiNewCreditCardWidget,
} from "@/components/forms/nuvei-new-credit-card-widget/NuveiNewCreditCardWidget";
import { useLogger } from "@/hooks/useLogger";
import { REQUIRED_PAYMENT_METHOD_FIELDS } from "@/utils/constants";
import { EFormIds } from "@/utils/enums";
import styles from "./DynamicForm.module.css";

const validate = (
  value: string,
  field: FormField,
  t: TFunction,
  logger: Logger | Console
): string | null => {
  const { validation_descriptor } = field;
  const {
    required,
    min_length,
    max_length,
    min,
    max,
    pattern,
    pattern_message,
    pattern_error,
    one_of,
  } = validation_descriptor ?? {};

  if (required && (!value || value.trim() === "")) {
    return t("deposit.errors.required");
  }

  if (typeof min_length !== "undefined" && value.length < min_length) {
    return t("deposit.errors.min_length", { min_length });
  }

  if (typeof max_length !== "undefined" && value.length > max_length) {
    return t("deposit.errors.max_length", { max_length });
  }

  if (
    typeof min !== "undefined" &&
    !Number.isNaN(Number(value)) &&
    Number(value) < Number(min)
  ) {
    return t("deposit.errors.min_value", { min });
  }

  if (
    typeof max !== "undefined" &&
    !Number.isNaN(Number(value)) &&
    Number(value) > Number(max)
  ) {
    return t("deposit.errors.max_value", { max });
  }

  if (pattern) {
    if (!value && pattern_error) {
      return pattern_error;
    }

    try {
      const regex = new RegExp(pattern);
      if (!regex.test(value)) {
        return pattern_message || t("deposit.errors.invalid_format");
      }
    } catch (e) {
      logger.error("Invalid regex pattern:", { pattern, e });
    }
  }

  if (one_of) {
    const allowed = one_of.split(",").map((v) => v.trim());
    if (value && !allowed.includes(value)) {
      return t("deposit.errors.invalid_selection");
    }
  }

  return null;
};

export type FieldOptionsFilter = (
  fieldId: string,
  options: Array<{ id: string; label: string }>,
  formData: Record<string, string>
) => Array<{ id: string; label: string }>;

export type DynamicFormProps = {
  id: string;
  fields: FormField[];
  className?: string;
  defaultValues?: { [key: string]: string | FormField["options"] };
  optionsFilter?: FieldOptionsFilter;
  onChange?: (value: string, id: string) => void;
  onChanges?: (
    value: Record<string, string | boolean | string[] | undefined>
  ) => void;
  onError?: (error: string, id: string) => void;
  onCreditCardChange?: (value: NuveiCardFieldsChange) => void;
  bankStatementFile?: UploadedFile;
  onBankStatementFileChange?: (file: UploadedFile | undefined) => void;
};

export const DynamicForm = ({
  id,
  fields,
  className,
  defaultValues,
  optionsFilter,
  onChange,
  onChanges,
  onError,
  onCreditCardChange,
  bankStatementFile,
  onBankStatementFileChange,
}: DynamicFormProps) => {
  const { t } = useTranslation();
  const logger = useLogger();

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});

  const getEffectiveFormData = (current: Record<string, string>) => {
    if (!defaultValues) return current;
    const merged: Record<string, string> = {};
    for (const [key, val] of Object.entries(defaultValues)) {
      if (typeof val === "string") merged[key] = val;
    }
    return { ...merged, ...current };
  };

  /**
   * Translation key declarations for i18next-parser extraction.
   * These keys are used dynamically in the form rendering with the form_fields prefix,
   * so we need to declare them statically here for the parser to detect them.
   * This code never executes but is required for the build process.
   */
  // t("form_fields.paypal.form.email.label");
  // t("form_fields.paypal.form.email.placeholder");
  // t("form_fields.neteller.form.email.label");
  // t("form_fields.neteller.form.email.placeholder");
  // t("form_fields.skrill.form.email.label");
  // t("form_fields.skrill.form.email.placeholder");
  // t("form_fields.bank.form.account_name.label");
  // t("form_fields.bank.form.account_name.placeholder");
  // t("form_fields.bank.form.account_number.label");
  // t("form_fields.bank.form.account_number.placeholder");
  // t("form_fields.bank.form.bank_name.label");
  // t("form_fields.bank.form.bank_name.placeholder");
  // t("form_fields.bank.form.branch_code.label");
  // t("form_fields.bank.form.branch_code.placeholder");
  // t("form_fields.bank.form.swift.label");
  // t("form_fields.bank.form.swift.placeholder");
  // t("form_fields.bank.form.additional_info.label");
  // t("form_fields.bank.form.additional_info.placeholder");
  // t("form_fields.bank.form.file.label");
  // t("form_fields.bank.form.file.placeholder");
  // t("form_fields.zotapay.form.account_number.label");
  // t("form_fields.zotapay.form.account_number.placeholder");
  // t("form_fields.zotapay.form.bank_name.label");
  // t("form_fields.zotapay.form.bank_name.placeholder");
  // t("form_fields.zotapay.form.account_type.label");
  // t("form_fields.zotapay.form.account_type.placeholder");
  // t("form_fields.naewe.form.network.label");
  // t("form_fields.naewe.form.network.placeholder");
  // t("form_fields.naewe.form.currency.label");
  // t("form_fields.naewe.form.currency.placeholder");
  // t("form_fields.naewe.form.wallet_address.label");
  // t("form_fields.naewe.form.wallet_address.placeholder");

  const computeIsValid = (
    currentFormData: Record<string, string>,
    allFields: FormField[]
  ): boolean => {
    return allFields.every((f) => {
      if (f.type === EnumFormFieldType.EnumFormFieldTypeNuveiCard) return true;
      const val = currentFormData[f.id] ?? "";
      return validate(val, f, t, logger) === null;
    });
  };

  const handleChange = (value: string, field: FormField) => {
    const error = validate(value, field, t, logger);

    if (error) {
      const updatedErrors = { ...errors, [field.id]: error };
      setErrors(updatedErrors);
      onError?.(error, field.id);
      onChanges?.({ ...formData, isValid: false });
    } else {
      const updatedFormData = {
        ...getEffectiveFormData(formData),
        [field.id]: value,
      };
      const clearedFieldIds: string[] = [];

      if (optionsFilter) {
        for (const otherField of fields) {
          if (
            otherField.id === field.id ||
            otherField.type !== EnumFormFieldType.EnumFormFieldTypeDropdown
          )
            continue;

          const currentValue = updatedFormData[otherField.id];
          if (!currentValue) continue;

          const allOptions = Object.entries(otherField.options ?? {}).map(
            ([key, label]) => ({ id: key, label: String(label) })
          );
          const filtered = optionsFilter(
            otherField.id,
            allOptions,
            updatedFormData
          );

          if (!filtered.some((opt) => opt.id === currentValue)) {
            updatedFormData[otherField.id] = "";
            clearedFieldIds.push(otherField.id);
          }
        }
      }

      const updatedErrors = { ...errors, [field.id]: "" };
      for (const clearedId of clearedFieldIds) {
        updatedErrors[clearedId] = "";
      }
      setFormData(updatedFormData);
      setErrors(updatedErrors);
      setDirty((prev) => {
        if (clearedFieldIds.length === 0) return prev;
        const next = { ...prev };
        for (const clearedId of clearedFieldIds) {
          next[clearedId] = false;
        }
        return next;
      });
      onChange?.(value, field.id);

      const isValid = computeIsValid(updatedFormData, fields);
      onChanges?.({ ...updatedFormData, isValid });
    }
  };

  const toggleDropdown = (fieldId: string, open: boolean) => {
    setDropdownOpen((prev) => ({ ...prev, [fieldId]: open }));
  };

  useEffect(() => {
    setDirty({});
    const hasNonCardFields = fields.some(
      (f) => f.type !== EnumFormFieldType.EnumFormFieldTypeNuveiCard
    );
    if (hasNonCardFields) {
      const isValid = fields
        .filter((f) => f.type !== EnumFormFieldType.EnumFormFieldTypeNuveiCard)
        .every((f) => validate("", f, t, logger) === null);
      onChanges?.({ isValid });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to update the isValid state for the required fields once, to check if we can proceed later
  useEffect(() => {
    const requiredDefaults = REQUIRED_PAYMENT_METHOD_FIELDS.reduce<
      Record<string, string>
    >((acc, fieldId) => ({ ...acc, [fieldId]: "" }), {});
    onChanges?.({ ...formData, ...requiredDefaults, isValid: false });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only sync when the bank statement file changes
  useEffect(() => {
    // Always send document_id so it overwrites any previously-set value in
    // downstream state: onChanges feeds updateForm, which merges payloads into
    // existing Redux state, so omitting the key would leave a stale document_id
    // behind. undefined clears it and is dropped during JSON serialization.
    const documentId = bankStatementFile?.document_id
      ? String(bankStatementFile.document_id)
      : undefined;
    onChanges?.({ ...formData, document_id: documentId });
  }, [bankStatementFile]);

  const handleNuveiCardChange = useCallback(
    (change: NuveiCardFieldsChange) => {
      setDirty((d) => ({ ...d, [EFormIds.CreditCard]: true }));
      onCreditCardChange?.(change);
    },
    [onCreditCardChange]
  );

  const renderField = (field: FormField) => {
    const commonKey = `${id}-${field.id}`;
    const error = dirty[field.id] ? errors[field.id] : "";

    const isLabelI18nKey = field.label.includes(".");
    const isPlaceholderI18nKey = field.placeholder.includes(".");

    const translatedLabel = isLabelI18nKey
      ? t(`form_fields.${field.label}`)
      : field.label;
    const translatedPlaceholder = isPlaceholderI18nKey
      ? t(`form_fields.${field.placeholder}`)
      : field.placeholder;

    switch (field.type) {
      case EnumFormFieldType.EnumFormFieldTypeText:
      case EnumFormFieldType.EnumFormFieldTypeNumber:
      case EnumFormFieldType.EnumFormFieldTypeEmail:
      case EnumFormFieldType.EnumFormFieldTypePassword:
      case EnumFormFieldType.EnumFormFieldTypeTel:
        return (
          <InputField
            key={commonKey}
            data-testid={`dynamic-field-${field.id} dynamic-field`}
            id={field.id}
            type={field.type}
            label={translatedLabel}
            value={formData[field.id] || ""}
            defaultValue={String(defaultValues?.[field.id] ?? "")}
            placeholder={translatedPlaceholder}
            fullWidth
            errorText={error || undefined}
            helperText={error ? undefined : translatedPlaceholder || ""}
            onBlur={(e) => {
              handleChange(e.target.value, field);
              setDirty((d) => ({ ...d, [field.id]: true }));
            }}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({ ...prev, [field.id]: value }));
            }}
            onWheel={
              field.type === EnumFormFieldType.EnumFormFieldTypeNumber
                ? (e) => e.currentTarget.blur()
                : undefined
            }
          />
        );

      case EnumFormFieldType.EnumFormFieldTypeDropdown: {
        const allOptions = Object.entries(field.options ?? {}).map(
          ([key, label]) => ({
            id: key,
            label: String(label),
          })
        );

        const defaultValue = defaultValues?.[field.id];
        const selectedValue =
          formData[field.id] ??
          (typeof defaultValue === "string" ? defaultValue : undefined);

        const options =
          optionsFilter && !selectedValue
            ? optionsFilter(
                field.id,
                allOptions,
                getEffectiveFormData(formData)
              )
            : allOptions;

        const selected =
          options.find((opt) => opt.id === selectedValue) || null;

        const isOpen = dropdownOpen[field.id] || false;

        return (
          // Wrap so we can validate on blur the same way InputField does:
          // DropdownList exposes no onBlur prop, and React's onBlur bubbles
          // from the trigger/option buttons (which all live inside this div).
          // The relatedTarget containment check skips validation while focus
          // moves between the trigger and its own options (e.g. mid-select),
          // so we never flash a "required" error during an in-progress choice.
          <div
            key={commonKey}
            className={styles.dropdownField}
            data-testid={`dynamic-field-${field.id} dynamic-field`}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                // Validate the effective value (defaults merged with formData) to
                // match the displayed `selected`, so a preselected default isn't
                // wrongly flagged as required.
                handleChange(
                  getEffectiveFormData(formData)[field.id] ?? "",
                  field
                );
                setDirty((d) => ({ ...d, [field.id]: true }));
              }
            }}
          >
            <DropdownList
              label={translatedLabel}
              placeholder={translatedPlaceholder}
              open={isOpen}
              onClick={() => toggleDropdown(field.id, !isOpen)}
              close={() => toggleDropdown(field.id, false)}
              selected={selected}
              onSelect={(option) => {
                handleChange(option.id, field);
                setDirty((d) => ({ ...d, [field.id]: true }));
                toggleDropdown(field.id, false);
              }}
              options={options}
              errorMessage={error || undefined}
            />
          </div>
        );
      }

      case EnumFormFieldType.EnumFormFieldTypeNuveiCard:
        return (
          <NuveiNewCreditCardWidget
            key={commonKey}
            onChange={handleNuveiCardChange}
            showError={dirty[EFormIds.CreditCard]}
          />
        );

      case EnumFormFieldType.EnumFormFieldTypeFile: {
        return (
          <BankStatementUpload
            label={translatedLabel}
            key={commonKey}
            file={bankStatementFile}
            onFileChange={onBankStatementFileChange ?? (() => {})}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={className ?? "ps-flex ps-flex-col ps-gap-3 ps-pb-6"}
    >
      {fields.map(renderField)}
    </form>
  );
};
